import React, { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { DSPreview } from './DSPreview';
import groupBoxesBySheet from '../development/pdf/dsGroupBySheet';
import { diffBoxes, diffValues, toHighlight } from './dsDiff';

const noop = () => {};

const nameOf = box => (box ? (box.label || box.labelIdx || box.fieldSource || box.type) : '');

const lookup = v => id => (v instanceof Map ? v.get(id) : v?.[id]);

/**
 * Shows two versions of one datasheet and lets the caller's actions decide between them.
 * The preview toggles between the two versions; differing boxes and values are highlighted
 * and a table lists every difference.
 *
 * Props: title, leftLabel, rightLabel, leftBoxes/rightBoxes (Composer box model),
 * leftValues/rightValues (box id -> value text, object or Map), sheetCount, layout (the
 * DSPreview layout for the sheet), leftEmptyNote/rightEmptyNote (shown instead of the preview
 * when that side has no datasheet), actions [{ label, onClick, primary?, disabled? }], onClose.
 */
export default function DsDiffViewerModal({
  title, leftLabel, rightLabel, leftBoxes, rightBoxes, leftValues, rightValues,
  leftEmptyNote = 'No datasheet on this side.', rightEmptyNote = 'No datasheet on this side.',
  sheetCount = 0, layout = null, actions = [], onClose,
}) {
  const [side, setSide] = useState('left');

  const { highlight, rows } = useMemo(() => {
    const all = [...(leftBoxes || []), ...(rightBoxes || [])];
    const idOf = new Map(all.map(b => [String(b.id), b.id]));
    const leftById = new Map((leftBoxes || []).map(b => [String(b.id), b]));
    const rightById = new Map((rightBoxes || []).map(b => [String(b.id), b]));
    const boxDiff = diffBoxes(leftBoxes, rightBoxes);
    const valueDiff = diffValues(leftValues, rightValues);
    // Value-diff keys may be strings (object keys); re-key them to the boxes' own ids so
    // DSPreview's Map lookup by box.id matches.
    const merged = toHighlight(boxDiff, valueDiff);
    const hl = new Map();
    merged.forEach((kind, id) => hl.set(idOf.get(String(id)) ?? id, kind));

    const list = [...hl.entries()].map(([id, kind]) => {
      const lb = leftById.get(String(id));
      const rb = rightById.get(String(id));
      const v = valueDiff.get(String(id)) || valueDiff.get(id);
      const state = diffBoxes(lb ? [lb] : [], rb ? [rb] : []).get(id);
      let left;
      let right;
      if (state === 'added') { left = '(not present)'; right = v ? v.right : '(added)'; }
      else if (state === 'removed') { left = v ? v.left : '(removed)'; right = '(not present)'; }
      else if (v) { left = v.left; right = v.right; }
      else { left = nameOf(lb); right = nameOf(rb); }
      return { id, kind, lineNo: (rb || lb)?.lineNo ?? '', label: nameOf(rb || lb), left, right };
    }).sort((a, b) => (Number(a.lineNo) || 0) - (Number(b.lineNo) || 0));
    return { highlight: hl, rows: list };
  }, [leftBoxes, rightBoxes, leftValues, rightValues]);

  const shownBoxes = useMemo(() => (side === 'left' ? leftBoxes : rightBoxes) || [], [side, leftBoxes, rightBoxes]);
  const shownValues = side === 'left' ? leftValues : rightValues;
  const draft = useMemo(() => {
    const get = lookup(shownValues);
    const o = {};
    shownBoxes.forEach(b => { const v = get(b.id); if (v !== undefined && v !== null) o[b.id] = v; });
    return o;
  }, [shownBoxes, shownValues]);

  const pages = groupBoxesBySheet(shownBoxes);

  return (
    <div className="stui-modal-overlay">
      <div className="stui-modal stui-modal-lg" style={{ maxWidth: 1180, width: '97vw' }}>
        <div className="stui-modal-header">
          <span className="stui-modal-title">{title}</span>
          <button className="stui-modal-close" onClick={onClose} title="Close"><X size={20} /></button>
        </div>

        <div className="stui-modal-body">
          <div className="stui-segmented">
            {[['left', leftLabel], ['right', rightLabel]].map(([v, l]) => (
              <button key={v} className={`stui-segmented-btn${side === v ? ' selected' : ''}`} onClick={() => setSide(v)}>{l}</button>
            ))}
          </div>
          <p className="stui-hint">
            {rows.length === 0 ? 'The two versions are identical.' : `${rows.length} difference${rows.length === 1 ? '' : 's'} highlighted.`}
          </p>

          {shownBoxes.length === 0 && (
            <div className="stui-detail-empty">{side === 'left' ? leftEmptyNote : rightEmptyNote}</div>
          )}
          {shownBoxes.length > 0 && pages.map(({ sheet, boxes }, i) => (
            <div key={sheet} style={{ marginBottom: i < pages.length - 1 ? 18 : 0 }}>
              {pages.length > 1 && <p className="stui-hint stui-fw-semibold">Content {sheet}</p>}
              <DSPreview
                boxes={boxes}
                draftValues={draft}
                setDraftValues={noop}
                resolveDataValue={box => String(draft[box.id] ?? '')}
                sheetCount={sheetCount || pages.length}
                layout={layout}
                highlight={highlight}
                readOnly
              />
            </div>
          ))}

          <p className="stui-hint stui-fw-semibold">Changes</p>
          {rows.length === 0 ? (
            <p className="stui-hint">No differences.</p>
          ) : (
            <div className="stui-data-grid-container">
              <div className="stui-data-grid-scroll-area">
                <table className="stui-data-grid">
                  <thead>
                    <tr><th>Line</th><th>Label</th><th>{leftLabel}</th><th>{rightLabel}</th></tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.id}><td>{r.lineNo}</td><td>{r.label}</td><td>{r.left}</td><td>{r.right}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="stui-modal-footer">
          <button className="stui-btn stui-btn-secondary" onClick={onClose}>Close</button>
          {actions.map(a => (
            <button key={a.label} className={`stui-btn ${a.primary ? 'stui-btn-primary' : 'stui-btn-secondary'}`} disabled={a.disabled} onClick={a.onClick}>{a.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
